<template>
  <article class="sale-summary-card sale-summary-card--payment-flow" :style="cardStyle">
    <header class="sale-summary-card__section-title">
      <h5>{{ title }}</h5>
    </header>

    <div class="sale-summary-payment-total">
      <span>{{ totalPendingLabel }}</span>
      <strong>{{ formatMoney(totalPending) }}</strong>
    </div>

    <div class="sale-summary-payment-list">
      <div class="sale-summary-payment-item">
        <p>
          <i class="fa fa-clock-o" aria-hidden="true"></i>
          <span>{{ overdueLabel }}</span>
        </p>
        <strong class="is-danger">{{ formatMoney(overdue) }}</strong>
      </div>

      <div class="sale-summary-payment-item">
        <p>
          <i class="fa fa-calendar-check-o" aria-hidden="true"></i>
          <span>{{ next7DaysLabel }}</span>
        </p>
        <strong>{{ formatMoney(next7Days) }}</strong>
      </div>

      <div class="sale-summary-payment-item">
        <p>
          <i class="fa fa-calendar" aria-hidden="true"></i>
          <span>{{ restOfMonthLabel }}</span>
        </p>
        <strong>{{ formatMoney(restOfMonth) }}</strong>
      </div>
    </div>
  </article>
</template>

<script>
import salesService from '@/services/sales';
import salesMultiCustomerService from '@/services/salesMultiCustomer';
import customersService from '@/services/customers';
import { extractPayload } from '@/utils/dataNormalization';
import { formatCurrency } from '@/utils/numberFormat';

const normalizeFlow = (payload) => ({
  pending_total: Number(payload?.pending_total || 0) || 0,
  pending_overdue: Number(payload?.pending_overdue || 0) || 0,
  pending_next_7_days: Number(payload?.pending_next_7_days || 0) || 0,
  pending_rest_of_month: Number(payload?.pending_rest_of_month || 0) || 0,
});

export default {
  name: 'PaymentFlowCard',
  props: {
    flow: {
      type: Object,
      default: null,
    },
    saleId: {
      type: Number,
      default: 0,
    },
    customerId: {
      type: Number,
      default: 0,
    },
    isMultiCustomerSale: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
      default: 'Flujo de pagos',
    },
    totalPendingLabel: {
      type: String,
      default: 'Total pendiente',
    },
    overdueLabel: {
      type: String,
      default: 'Vencidos (+30d)',
    },
    next7DaysLabel: {
      type: String,
      default: 'Próximos 7 días',
    },
    restOfMonthLabel: {
      type: String,
      default: 'Resto del mes',
    },
    topBorderColor: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      flowState: normalizeFlow(null),
    };
  },
  computed: {
    hasProvidedFlow() {
      return this.flow && typeof this.flow === 'object';
    },
    totalPending() {
      return Number(this.flowState?.pending_total || 0) || 0;
    },
    overdue() {
      return Number(this.flowState?.pending_overdue || 0) || 0;
    },
    next7Days() {
      return Number(this.flowState?.pending_next_7_days || 0) || 0;
    },
    restOfMonth() {
      return Number(this.flowState?.pending_rest_of_month || 0) || 0;
    },
    cardStyle() {
      const color = String(this.topBorderColor || '').trim();
      if (color === '') {
        return {};
      }

      return {
        borderTopColor: color,
      };
    },
  },
  watch: {
    flow: {
      immediate: true,
      deep: true,
      handler() {
        this.loadFlow();
      },
    },
    saleId() {
      this.loadFlow();
    },
    customerId() {
      this.loadFlow();
    },
    isMultiCustomerSale() {
      this.loadFlow();
    },
  },
  methods: {
    async loadFlow() {
      if (this.hasProvidedFlow) {
        this.flowState = normalizeFlow(this.flow);
        return;
      }

      const customerId = Number(this.customerId || 0);
      if (customerId > 0) {
        await this.loadCustomerFlow(customerId);
        return;
      }

      const saleId = Number(this.saleId || 0);
      if (saleId > 0) {
        await this.loadSaleFlow(saleId);
        return;
      }

      this.resetFlow();
    },
    async loadSaleFlow(saleId) {
      try {
        const response = this.isMultiCustomerSale
          ? await salesMultiCustomerService.paymentFlow(saleId)
          : await salesService.paymentFlow(saleId);

        const payload = extractPayload(response);
        this.flowState = normalizeFlow(payload);
      } catch (error) {
        this.resetFlow();
      }
    },
    async loadCustomerFlow(customerId) {
      try {
        const response = await customersService.detailOverview(customerId);
        const payload = extractPayload(response);
        this.flowState = normalizeFlow(payload?.payment_flow);
      } catch (error) {
        this.resetFlow();
      }
    },
    resetFlow() {
      this.flowState = normalizeFlow(null);
    },
    formatMoney(value) {
      return formatCurrency(Number(value || 0) || 0);
    },
  },
};
</script>